/**
 * Parent-listing (listing_to_listing) bulk assignment payloads.
 */

/**
 * Builds the setValue payload for assigning one child listing to a set of parents.
 * Parents the row currently has but that aren't in `parentM1Keys` are set false;
 * the selected parents are set true. A parent in both collapses to true.
 *
 * @param {Object} row - listing row; reads `uuid` and `parent_listings`
 * @param {string[]} parentM1Keys - parent M1 keys (`listing.<uuid>`) to end up assigned
 * @returns {Object} entityName -> boolean, empty when there is nothing to write
 */
export const buildParentAssignmentPayload = (row, parentM1Keys) => {
	if (!row || !row.uuid) return {};

	const payload = {};

	(row.parent_listings || []).forEach((parentM1Key) => {
		payload[`${parentM1Key}.listing.${row.uuid}`] = false;
	});

	(parentM1Keys || []).forEach((parentM1Key) => {
		payload[`${parentM1Key}.listing.${row.uuid}`] = true;
	});

	return payload;
};

/**
 * Counts, per parent M1 key, how many `listing.<uuid>` M2s a payload carries.
 * The server writes only the first M2 it finds under a parent M1 key, so any count
 * above 1 means silently dropped relations. Used to guard the one-row-per-save rule.
 *
 * @param {Object} payload - entityName -> boolean
 * @returns {Object} `listing.<parentUUID>` -> number of child M2s
 */
export const countM2sPerParent = (payload) => {
	const counts = {};

	Object.keys(payload || {}).forEach((entityName) => {
		const parts = entityName.split('.');
		if (parts.length !== 4) return;

		const parentM1Key = `${parts[0]}.${parts[1]}`;
		counts[parentM1Key] = (counts[parentM1Key] || 0) + 1;
	});

	return counts;
};

/**
 * Picks the listing whose concatenated columns must be regenerated after a
 * pending edit keyed by `pendingKey` is saved.
 *
 * Parent assignments arrive as `listing.<parent>.listing.<child>`; the
 * regenerator only touches listings in the current (child) tier, so the child
 * uuid is returned. Column edits (`listing.<uuid>.listing_column.<col>`) and
 * bare `listing.<uuid>` keys return the listing at index 1.
 *
 * @param {string} pendingKey - dotted pendingUpdates key
 * @returns {string|undefined} listing uuid to regenerate
 */
export const listingUUIDForConcatRegen = (pendingKey) => {
	const parts = String(pendingKey || '').split('.');
	return parts[2] === 'listing' && parts[3] ? parts[3] : parts[1];
};
